import {
	Box,
	Collapse,
	Paper,
	Skeleton,
	SvgIcon,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	type BoxProps,
	type TableBodyProps,
	type TableCellProps,
	type TableContainerProps,
	type TableHeadProps,
	type TableProps,
	type TableRowProps,
} from "@mui/material";
import * as React from "react";
import NoResultImage from "../table/images/table-no-results-available.svg";
import CaretRight from "../../images/caret-right.svg?react";
import CaretDown from "../../images/caret-down.svg?react";
import { stableSort, TableSortLabelIcon } from "../table/Table";
import { CoreIconButton } from "../button/Button.tsx";
import styles from "./CoreDataView.module.css";

export type SortDirection = "asc" | "desc";

export type DataKey<T> = keyof T;
export interface SortConfig<T> {
	key: DataKey<T>;
	direction: SortDirection;
}

export interface CoreDataViewColumn<T> {
	key: DataKey<T>;
	header: React.ReactNode;
	cell: (item: T) => React.ReactNode;
	width?: string | number;
	padding?: string | number;
	sortable?: boolean;
	tooltip?: boolean;
	allowOverflow?: boolean;
}

export interface CoreDataViewProps<T> {
	/** The data to display */
	data: T[];
	/** Column definitions */
	columns: CoreDataViewColumn<T>[];
	/** Loading state */
	loading?: boolean;
	/** Number of skeleton rows to show when loading */
	skeletonRowCount?: number;
	/** Current sort configuration (supports multiple sort keys) */
	sortConfig?: SortConfig<T>[];
	/** Handler for sort changes */
	onSortChange?: (sortConfig: SortConfig<T>[]) => void;
	/** Custom class name */
	className?: string;
	/** No results message */
	noResultsMessage?: string;
	/** No results description */
	noResultsDescription?: string;
	/** Handler for row click */
	onRowClick?: (item: T) => void;
	/**Id of table */
	id?: string;
	/** Slots for table components */
	slots?: {
		tableWrapper?: BoxProps;
		tableContainer?: TableContainerProps;
		table?: TableProps;
		tableHead?: TableHeadProps;
		tableHeadRow?: TableRowProps;
		tableHeadCell?: TableCellProps;
		tableBody?: TableBodyProps;
		tableBodyRow?: TableRowProps;
		tableBodyCell?: TableCellProps;
		tableNoResultsCell?: TableCellProps;
	};
	renderers?: {
		noResults?: React.ReactNode;
		rowTooltip?: (item: T) => React.ReactNode;
	};
	/** For Accessibility */
	tableRowRole?: React.AriaRole;
	/**For Accessibility */
	getRowAriaLabel?: (item: T) => string;
	/** Renders a collapsible row*/
	renderCollapsibleRow?: (item: T) => React.ReactNode;
	/** Defines the id of the row */
	getRowId?: (item: T) => number;
	/** Disables the component's internal sorting*/
	disableInternalSorting?: boolean;
	/** Render all column headers as <strong> elements */
	strongHeaders?: boolean;
}

export function CoreDataView<T>({
	id,
	data,
	columns,
	loading = false,
	skeletonRowCount = 5,
	sortConfig = [],
	onSortChange,
	className,
	noResultsMessage = "No data available",
	noResultsDescription = "Try adjusting your search criteria and filters.",
	onRowClick,
	slots,
	renderers,
	tableRowRole,
	getRowAriaLabel,
	renderCollapsibleRow,
	getRowId,
	disableInternalSorting,
	strongHeaders,
}: CoreDataViewProps<T>) {
	const [openRows, setOpenRows] = React.useState<Set<number>>(new Set());

	const handleSortClick = (key: DataKey<T>) => {
		if (!onSortChange) return;

		const existingSortIndex = sortConfig.findIndex((config) => config.key === key);
		const newSortConfig = [...sortConfig];

		if (existingSortIndex >= 0) {
			const currentDirection = sortConfig[existingSortIndex].direction;
			if (currentDirection === "asc") {
				newSortConfig[existingSortIndex] = { key, direction: "desc" };
			} else if (currentDirection === "desc") {
				newSortConfig.splice(existingSortIndex, 1);
			}
		} else {
			newSortConfig.push({ key, direction: "asc" });
		}

		onSortChange(newSortConfig);
	};

	const sortedData = React.useMemo(() => {
		if (!sortConfig.length || disableInternalSorting) return data;
		const { key, direction } = sortConfig[0];
		const mult = direction === "asc" ? 1 : -1;
		const comparator = (a: T, b: T): number => {
			const aVal = a[key] as T;
			const bVal = b[key] as T;

			if (aVal == null) return -1 * mult;
			if (bVal == null) return 1 * mult;

			if (aVal < bVal) return -1 * mult;
			if (aVal > bVal) return 1 * mult;
			return 0;
		};

		return stableSort(data, comparator);
	}, [data, disableInternalSorting, sortConfig]);

	const getSortDirection = (key: DataKey<T>): SortDirection | undefined => {
		const sort = sortConfig.find((config) => config.key === key);
		return sort ? sort.direction : undefined;
	};

	const isSorted = (key: DataKey<T>): boolean => {
		return sortConfig.some((config) => config.key === key);
	};

	const toggleRow = (index: number) => {
		setOpenRows((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	};

	return (
		<Box className={styles.tableWrapper} {...slots?.tableWrapper}>
			<TableContainer
				component={Paper}
				className={`${styles.tableContainer} ${className || ""}`}
				{...slots?.tableContainer}
			>
				<Table className={styles.table} id={id} {...slots?.table}>
					{loading ||
						(sortedData.length > 0 && (
							<TableHead className={styles.tableHead} {...slots?.tableHead}>
								<TableRow {...slots?.tableHeadRow}>
									{renderCollapsibleRow && <TableCell className={`${styles.tableHeadCell}`} />}
									{columns.map((column) => {
										const header = strongHeaders ? <strong>{column.header}</strong> : column.header;

										return (
											<TableCell
												key={column.key.toString()}
												style={{ width: column.width, padding: column.padding }}
												className={styles.tableHeadCell}
												sortDirection={column.sortable && isSorted(column.key) ? getSortDirection(column.key) : false}
												aria-sort={
													column.sortable && isSorted(column.key)
														? getSortDirection(column.key) === "asc"
															? "ascending"
															: "descending"
														: undefined
												}
												{...slots?.tableHeadCell}
											>
												{column.sortable ? (
													<TableSortLabel
														onClick={() => handleSortClick(column.key)}
														active={isSorted(column.key)}
														direction={getSortDirection(column.key)}
														disabled={!column.sortable}
														IconComponent={TableSortLabelIcon}
													>
														{header}
													</TableSortLabel>
												) : (
													header
												)}
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
						))}

					{loading ? (
						<TableBody {...slots?.tableBody}>
							{Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
								<TableRow key={`skeleton-${rowIndex}`} className={styles.tableRow} {...slots?.tableBodyRow}>
									{columns.map((_, colIndex) => (
										<TableCell
											key={`skeleton-${rowIndex}-${colIndex}`}
											className={styles.tableCell}
											{...slots?.tableBodyCell}
										>
											<Skeleton variant="rounded" animation="wave" />
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					) : sortedData.length > 0 ? (
						<TableBody {...slots?.tableBody}>
							{sortedData.map((item, index) => {
								const rowIndex = getRowId ? getRowId(item) : index;
								return (
									<>
										<Tooltip key={`table-row-${index}-tooltip`} title={renderers?.rowTooltip?.(item)} followCursor>
											<TableRow
												key={index}
												className={`${styles.tableRow} ${onRowClick ? styles.clickableRow : ""}`}
												onClick={() => {
													onRowClick?.(item);
													renderCollapsibleRow && toggleRow(rowIndex);
												}}
												{...slots?.tableBodyRow}
												tabIndex={onRowClick ? 0 : undefined}
												onKeyDown={(e) => {
													if (onRowClick && (e.key === "Enter" || e.key === " ")) {
														e.preventDefault();
														onRowClick(item);
														renderCollapsibleRow && toggleRow(rowIndex);
													}
												}}
												role={tableRowRole}
												aria-label={getRowAriaLabel ? getRowAriaLabel(item) : undefined}
											>
												{renderCollapsibleRow && (
													<TableCell className={styles.toggleCell} {...slots?.tableBodyCell}>
														<CoreIconButton variant="plain" color="currentColor">
															<SvgIcon style={{ display: "flex", fontSize: "16px" }}>
																{openRows.has(rowIndex) ? <CaretDown /> : <CaretRight />}
															</SvgIcon>
														</CoreIconButton>
													</TableCell>
												)}
												{columns.map((column) =>
													column.tooltip ? (
														<Tooltip key={`${index}-${column.key.toString()}`} title={column.cell(item)}>
															<TableCell
																className={`${styles.tableCell} ${column.allowOverflow ? styles.overflowCell : ""}`}
																style={{ width: column.width, padding: column.padding }}
																{...slots?.tableBodyCell}
															>
																{column.cell(item)}
															</TableCell>
														</Tooltip>
													) : (
														<TableCell
															key={`${index}-${column.key.toString()}`}
															className={`${styles.tableCell} ${column.allowOverflow ? styles.overflowCell : ""}`}
															style={{ width: column.width, padding: column.padding }}
															{...slots?.tableBodyCell}
														>
															{column.cell(item)}
														</TableCell>
													),
												)}
											</TableRow>
										</Tooltip>
										{renderCollapsibleRow && (
											<TableRow {...slots?.tableBodyRow}>
												<TableCell className={styles.collapsibleCell} colSpan={columns.length}>
													<Collapse
														className={styles.collapsibleElement}
														in={openRows.has(rowIndex)}
														timeout="auto"
														unmountOnExit
														sx={{
															"& .MuiCollapse-wrapperInner": {
																padding: "38px",
															},
														}}
													>
														{renderCollapsibleRow(item)}
													</Collapse>
												</TableCell>
											</TableRow>
										)}
									</>
								);
							})}
						</TableBody>
					) : (
						<TableCell colSpan={columns.length} {...slots?.tableNoResultsCell}>
							{renderers && renderers.noResults ? (
								renderers.noResults
							) : (
								<div className={styles.noResultsContainer}>
									<img src={NoResultImage} alt="No results" className={styles.noResultsImage} />
									<span className={styles.noResultsText}>{noResultsMessage}</span>
									<span className={styles.noResultsDescription}>{noResultsDescription}</span>
								</div>
							)}
						</TableCell>
					)}
				</Table>
			</TableContainer>
		</Box>
	);
}
